/**
 * Client accessors for the landed live-service surface (backend `LiveServiceController` +
 * `LiveReadController`, DOMAIN.md §C.7). Every call funnels through `@repo/api-client`'s fetch mutator
 * (bearer token, RFC 9457 `ApiError`, auto Idempotency-Key on writes) and is typed by the
 * ORVAL-GENERATED models (regenerated by `just gen` — never hand-edited). Thin accessors (not the
 * generated hooks) so queries key under the shell's `["venue", venueId, "bookings", …]` convention and
 * realtime `booking.*` invalidation reaches the timeline + floor through one wiring.
 */
import { customInstance } from "@repo/api-client";
import type {
  LiveFloorResponse,
  LiveTimelineRowResponse,
  SeatRequest,
  StaffBookingResponse,
  TableReadyResponse,
  WalkInRequest,
} from "@repo/api-client";

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/live`;

export const liveApi = {
  /** Today's bookings + walk-ins for the venue-TZ service day, ordered by slot. */
  timeline: (venueId: string, serviceDay: string, signal?: AbortSignal) =>
    customInstance<LiveTimelineRowResponse[]>(
      `${base(venueId)}/timeline?serviceDay=${encodeURIComponent(serviceDay)}`,
      { method: "GET", signal },
    ),

  /** The live floor: per-table derived/recorded state + per-zone occupancy. */
  floor: (venueId: string, signal?: AbortSignal) =>
    customInstance<LiveFloorResponse>(`${base(venueId)}/floor`, { method: "GET", signal }),

  /** Arrive — a check-in timestamp only (§C.7). CAS on `version` (409 on conflict). */
  arrive: (venueId: string, bookingId: string, version: number) =>
    post<StaffBookingResponse>(
      `${base(venueId)}/bookings/${encodeURIComponent(bookingId)}/arrive`,
      { version },
    ),

  /** Seat onto tables (empty = counter seat). Same-target no-op; 409 on conflict. */
  seat: (venueId: string, bookingId: string, body: SeatRequest) =>
    post<StaffBookingResponse>(
      `${base(venueId)}/bookings/${encodeURIComponent(bookingId)}/seat`,
      body,
    ),

  /** Move a SEATED party to different tables (relocation). Same-target no-op; 409 on conflict/stale. */
  move: (venueId: string, bookingId: string, body: SeatRequest) =>
    post<StaffBookingResponse>(
      `${base(venueId)}/bookings/${encodeURIComponent(bookingId)}/move`,
      body,
    ),

  /** Depart — SEATED -> COMPLETED, the table flips to CLEANING. */
  depart: (venueId: string, bookingId: string, version: number) =>
    post<StaffBookingResponse>(
      `${base(venueId)}/bookings/${encodeURIComponent(bookingId)}/depart`,
      { version },
    ),

  /** No-show — CONFIRMED -> NO_SHOW, releases the claim. */
  noShow: (venueId: string, bookingId: string, version: number) =>
    post<StaffBookingResponse>(
      `${base(venueId)}/bookings/${encodeURIComponent(bookingId)}/no-show`,
      { version },
    ),

  /** Table-ready — close the cleaning fact so the table derives back to available. */
  tableReady: (venueId: string, tableId: string) =>
    post<TableReadyResponse>(
      `${base(venueId)}/tables/${encodeURIComponent(tableId)}/ready`,
      undefined,
    ),

  /** Door walk-in (channel WALK_IN) — consumes real inventory; may be created SEATED. */
  walkIn: (venueId: string, body: WalkInRequest) =>
    post<StaffBookingResponse>(`${base(venueId)}/walk-ins`, body),
};

function post<T>(url: string, body: unknown): Promise<T> {
  return customInstance<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? "{}" : JSON.stringify(body),
  });
}
